import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  Environment,
  ENVIRONMENT_TOKEN,
} from '../../../../apps/values/environment.token';
import { Assessment, ConsentRefusal } from '../../../../apps/types/assessment';
import { DeleteManyResult, MongoDBHttpService } from './mongodb-http/mongodb-http.service';
import { TEST_ORGANISATION_CODE } from '../../../../apps/values/participating-trusts';

@Injectable({
  providedIn: 'root',
})
export class AssessmentServiceNew {
  private _mongoHttpService = inject(MongoDBHttpService);
  private _environmentToken: Environment = inject(ENVIRONMENT_TOKEN);

  constructor() {}

  addAIneligibleAssessment(ineligibleAssessment: Assessment): Observable<any> {
    return this._mongoHttpService
      .db(this._environmentToken.databaseName)
      .collection<Assessment>(
        this._environmentToken.ineligibleAssessmentCollection!
      )
      .insertOne({
        ...ineligibleAssessment,
      });
  }

  getIneligibleAssessmentListByTherapistIdList(
    therapistIdList: string[]
  ): Observable<Array<Assessment>> {
    return this._mongoHttpService
      .db(this._environmentToken.databaseName)
      .collection<Assessment>(
        this._environmentToken.ineligibleAssessmentCollection!
      )
      .find({
        creator_user_id: { $in: therapistIdList },
      });
  }

  getIneligibleAssessmentListWithLimit(): Observable<Array<Assessment>> {
    return this._mongoHttpService
      .db(this._environmentToken.databaseName)
      .collection<Assessment>(
        this._environmentToken.ineligibleAssessmentCollection!
      )
      .find({});
  }

  addAssessmentConsentRefusal(consentRefusal: ConsentRefusal): Observable<any> {
    return this._mongoHttpService
      .db(this._environmentToken.databaseName)
      .collection<ConsentRefusal>(
        this._environmentToken.consentRefusalCollection!
      )
      .insertOne({
        ...consentRefusal,
      });
  }

  getAssessmentConsentRefusal(
    auth0UserId: string
  ): Observable<Array<ConsentRefusal>> {
    return this._mongoHttpService
      .db(this._environmentToken.databaseName)
      .collection<ConsentRefusal>(
        this._environmentToken.consentRefusalCollection!
      )
      .find({
        therapist_owner_user_id: auth0UserId,
      });
  }

  getAssessmentConsentRefusalListByTherapistIdList(
    therapistIdList: string[]
  ): Observable<Array<ConsentRefusal>> {
    return this._mongoHttpService
      .db(this._environmentToken.databaseName)
      .collection<ConsentRefusal>(
        this._environmentToken.consentRefusalCollection!
      )
      .find({
        therapist_owner_user_id: { $in: therapistIdList },
      });
  }

  getConsentRefusalListWithLimit(): Observable<Array<ConsentRefusal>> {
    return this._mongoHttpService
      .db(this._environmentToken.databaseName)
      .collection<ConsentRefusal>(
        this._environmentToken.consentRefusalCollection!
      )
      .find({});
  }

  getIAPT_IdCount(iaptID: string, orgGroupCode: string): Observable<number> {
    return this._mongoHttpService
      .db(this._environmentToken.databaseName)
      .collection<Assessment>(this._environmentToken.assessmentsCollection)
      .count({
        iapt_id: iaptID,
        org_group_code: orgGroupCode,
      });
  }

  updateAssessment(assessment: Assessment): Observable<any> {
    // Destructure _id from assessment to exclude it from the update
    // MongoDB doesn't allow updating the immutable _id field
    const { _id, ...assessmentWithoutId } = assessment;

    return this._mongoHttpService
      .db(this._environmentToken.databaseName)
      .collection<Assessment>(this._environmentToken.assessmentsCollection)
      .updateOne(
        {
          _id: _id,
        },
        {
          $set: {
            ...assessmentWithoutId,
          },
        },
        {
          upsert: true,
        }
      );
  }

  getAssessmentListByTherapist(auth0UserId: string): Observable<any> {
    return this._mongoHttpService
      .db(this._environmentToken.databaseName)
      .collection<Assessment>(this._environmentToken.assessmentsCollection)
      .find({
        creator_user_id: auth0UserId,
      });
  }

  getAllAssessmentList(): Observable<Array<Assessment>> {
    return this._mongoHttpService
      .db(this._environmentToken.databaseName)
      .collection<Assessment>(this._environmentToken.assessmentsCollection)
      .find({});
  }

  getTherapistsEnrolledByAdminAssessmentList(
    therapistIdList: Array<string>
  ): Observable<any> {
    return this._mongoHttpService
      .db(this._environmentToken.databaseName)
      .collection<Assessment>(this._environmentToken.assessmentsCollection)
      .find({
        creator_user_id: { $in: [...therapistIdList] },
      });
  }

  // ============================================================
  // TEST ORGANISATION DATA MANAGEMENT METHODS
  // All methods below are HARDCODED to Test Organisation (code "21")
  // for safety - prevents accidental deletion of real data
  // ============================================================

  /**
   * Count assessments for Test Organisation only.
   * Hardcoded to org_group_code "21" for safety.
   */
  getTestOrgAssessmentCount(): Observable<number> {
    return this._mongoHttpService
      .db(this._environmentToken.databaseName)
      .collection<Assessment>(this._environmentToken.assessmentsCollection)
      .count({
        org_group_code: TEST_ORGANISATION_CODE,
      });
  }

  /**
   * Count ineligible assessments for Test Organisation only.
   * Hardcoded to org_group_code "21" for safety.
   */
  getTestOrgIneligibleAssessmentCount(): Observable<number> {
    return this._mongoHttpService
      .db(this._environmentToken.databaseName)
      .collection<Assessment>(
        this._environmentToken.ineligibleAssessmentCollection!
      )
      .count({
        org_group_code: TEST_ORGANISATION_CODE,
      });
  }

  /**
   * Count consent refusals for Test Organisation therapists.
   * Therapist IDs should already be filtered to Test Organisation in the calling component.
   */
  getTestOrgConsentRefusalCount(therapistIds: string[]): Observable<number> {
    if (therapistIds.length === 0) {
      return new Observable((subscriber) => {
        subscriber.next(0);
        subscriber.complete();
      });
    }
    return this._mongoHttpService
      .db(this._environmentToken.databaseName)
      .collection<ConsentRefusal>(
        this._environmentToken.consentRefusalCollection!
      )
      .count({
        therapist_owner_user_id: { $in: therapistIds },
      });
  }

  /**
   * Delete all assessments for Test Organisation only.
   * Hardcoded to org_group_code "21" for safety.
   */
  deleteTestOrgAssessments(): Observable<DeleteManyResult> {
    return this._mongoHttpService
      .db(this._environmentToken.databaseName)
      .collection<Assessment>(this._environmentToken.assessmentsCollection)
      .deleteMany({
        org_group_code: TEST_ORGANISATION_CODE,
      });
  }

  /**
   * Delete all ineligible assessments for Test Organisation only.
   * Hardcoded to org_group_code "21" for safety.
   */
  deleteTestOrgIneligibleAssessments(): Observable<DeleteManyResult> {
    return this._mongoHttpService
      .db(this._environmentToken.databaseName)
      .collection<Assessment>(
        this._environmentToken.ineligibleAssessmentCollection!
      )
      .deleteMany({
        org_group_code: TEST_ORGANISATION_CODE,
      });
  }

  /**
   * Delete consent refusals for Test Organisation therapists.
   * Therapist IDs should already be filtered to Test Organisation in the calling component.
   */
  deleteTestOrgConsentRefusals(therapistIds: string[]): Observable<DeleteManyResult> {
    if (therapistIds.length === 0) {
      return new Observable((subscriber) => {
        subscriber.next({ deletedCount: 0 });
        subscriber.complete();
      });
    }
    return this._mongoHttpService
      .db(this._environmentToken.databaseName)
      .collection<ConsentRefusal>(
        this._environmentToken.consentRefusalCollection!
      )
      .deleteMany({
        therapist_owner_user_id: { $in: therapistIds },
      });
  }
}
